<template>
  <v-container class="order-container">
    <v-layout
      row
      wrap
    >
      <v-flex xs12>
        <v-btn
          flat
          class="btn__back px-0 mx-2"
          @click="$router.push('/pedidos')"
        >
          <v-icon
            color="#140C22"
            class="mr-3 mb-1"
          >
            $vuetify.icons.arrowLeftIcon
          </v-icon>
          Voltar
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout
      row
      wrap
      class="mt-3"
    >
      <v-flex
        v-if="order.resolved"
        xs12
        class="customer-title pl-3 mb-2"
      >
        <v-icon
          medium
          color="#6ADDC8"
          class="mr-2"
        >
          $vuetify.icons.checkIcon
        </v-icon>
        Pedido Resolvido
      </v-flex>
      <v-flex
        xs6
        class="customer-title pl-3"
      >
        Telefone do cliente: <br>
        {{ order.fullNumber }}
      </v-flex>
      <v-flex
        xs6
        style="text-align: end"
      >
        <v-btn
          v-if="!order.resolved"
          color="#FFADDE"
          depressed
          :href="getWhatsappURL()"
          target="_blank"
          class="btn__whatsapp-redirect"
        >
          <v-icon
            small
            class="mr-2"
          >
            mdi-whatsapp
          </v-icon>
          Confirmar Pedido
        </v-btn>
      </v-flex>
    </v-layout>
    <v-divider
      class="divider"
    />
    <v-card
      v-for="(product, idx) in order.cart.products"
      :key="product.sku"
      class="product-card"
      min-height="160px"
      height="fit-content"
    >
      <v-layout
        row
        wrap
      >
        <v-flex
          xs3
          class="d-flex"
        >
          <v-img
            :src="products[idx].mainImage ? products[idx].mainImage : 'https://konselior.s3.amazonaws.com/media/semImagem.jpg'"
            alt="product-image"
            contain
          />
        </v-flex>
        <v-spacer />
        <v-flex xs8>
          <v-card-title class="product-card__title mb-2">
            {{ products[idx].descricaoCurta }}
          </v-card-title>
          <v-card-text class="product-card__content">
            SKU do produto: {{ product.sku }}<br>
            Preço do produto: R${{ product.price.toLocaleString() }}<br>
            Quantidade do produto: {{ product.quantity }}
          </v-card-text>
        </v-flex>
      </v-layout>
    </v-card>
    <v-btn
      v-if="!order.resolved"
      class="btn__exclude ma-0"
      depressed
      color="#FFF"
      :loading="loadingExclude"
      @click="excludeDialog = true"
    >
      Excluir Pedido
    </v-btn>
    <v-btn
      class="btn__change-status ma-0"
      color="#140C22"
      dark
      :loading="loadingChangeStatus"
      @click="changeStatusDialog = true"
    >
      {{ orderStatusAction }}
    </v-btn>
    <v-dialog
      v-model="changeStatusDialog"
      max-width="317"
    >
      <ActionsDialog
        :dialog-text="`Deseja atualizar o pedido para “${orderChangeStatus}”?`"
        :first-action="() => { changeStatusDialog = false }"
        :second-action="changeOrderResolvedStatus"
        first-text="Cancelar"
        :second-text="orderStatusAction"
        :close="() => { changeStatusDialog = false }"
      />
    </v-dialog>
    <v-dialog
      v-model="excludeDialog"
      max-width="317"
    >
      <ActionsDialog
        dialog-text="Tem certeza que deseja excluir esse pedido?"
        :first-action="() => { excludeDialog = false }"
        :second-action="excludeOrder"
        first-text="Cancelar"
        second-text="Excluir"
        :close="() => { excludeDialog = false }"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import Promise from 'bluebird';
import { EventBus } from '@/event-bus.js';
import { mapGetters } from 'vuex';
import { useZipperApi } from '../composables/useZipperApi';

const { client } = useZipperApi();

export default {
  data: () => ({
    order: {},
    loadingChangeStatus: false,
    loadingExclude: false,
    changeStatusDialog: false,
    excludeDialog: false,
    products: [],
  }),
  computed: {
    ...mapGetters(['getOrders']),
    orderChangeStatus() {
      return this.order.resolved ? 'Aberto' : 'Resolvido';
    },
    orderStatusAction() {
      return `${this.order.resolved ? 'Reabrir' : 'Resolver'} Pedido`;
    },
    sanitizedPhone() {
      return this.order.fullNumber.match(/\d/g).join('');
    },
    decodedId() {
      return atob(this.$route.params.id);
    },
  },
  async mounted() {
    [this.order] = this.getOrders[this.decodedId];
    await this.getProductBySKU();
  },
  methods: {
    emitSnackbar(text) {
      const snackBar = {};
      snackBar.value = true;
      snackBar.text = text;
      snackBar.color = '#140C22';
      snackBar.button = false;
      snackBar.icon = '$vuetify.icons.checkIcon';
      snackBar.class = {
        height: '120px',
        'font-size': '16px',
        'font-weight': '600',
        'justify-content': 'center',
      };
      EventBus.$emit('activeSnackBar', snackBar);
    },
    async changeOrderResolvedStatus() {
      this.loadingChangeStatus = true;
      this.changeStatusDialog = false;
      await client.chain.mutation
        .changeOrderResolvedStatus({ orderId: this.decodedId })
        .get(true);
      this.emitSnackbar(`Pedido ${this.order.resolved ? 'reaberto' : 'resolvido'} com sucesso`);
      this.order.resolved = !this.order.resolved;
      this.loadingChangeStatus = false;
    },
    async excludeOrder() {
      this.loadingExclude = true;
      this.excludeDialog = false;
      await client.chain.mutation
        .deleteOrderById({ orderId: this.decodedId })
        .get(true);
      this.emitSnackbar('Pedido excluído com sucesso');
      this.order.resolved = !this.order.resolved;
      this.loadingExclude = false;
      this.$router.push('/pedidos');
    },
    async getProductBySKU() {
      await Promise.map(this.order.cart.products, async (product) => {
        const res = await client.chain.query
          .productByIdERP({ idERP: product.sku })
          .get({ mainImage: true, descricaoCurta: true });
        this.products.push(res);
      });
    },
    getWhatsappURL() {
      let value = 'Olá, vim confirmar seu pedido feito através do Shops:\n';
      this.order.cart.products.forEach((product, idx) => {
        value += `
          *SKU:* ${product.sku}
          *Nome:* ${this.products[idx].descricaoCurta}
          *Preço:* R$ ${product.price.toLocaleString()}
          *Quantidade:* ${product.quantity}
        `;
      });
      value += '\nVamos finalizá-lo agora? \u26A1';
      const url = `https://api.whatsapp.com/send?phone=+55${this.sanitizedPhone}&text=${encodeURIComponent(value)}`;
      return url;
    },
  },
};
</script>

<style lang="scss" scoped>

.btn {
  &__back {
    font-family: 'Neue-Metana';
    font-size: 12px;
    text-transform: capitalize;
    border-radius: 7px;
  }
  &__change-status {
    position: fixed;
    bottom: 80px;
    left: 0;
    width: 100%;
    font-family: 'Neue-Metana';
    font-weight: 400;
    font-size: 16px;
    line-height: 18px;
    text-transform: capitalize;
    height: 52px;
  }
  &__exclude {
    position: fixed;
    bottom: 132px;
    left: 0;
    width: 100%;
    font-family: 'Nunito Sans';
    font-weight: 400;
    font-size: 17px;
    line-height: 23px;
    text-transform: capitalize;
    height: 52px;
  }
  &__whatsapp-redirect {
    font-family: 'Nunito Sans';
    font-weight: 600;
    font-size: 14px;
    line-height: 102%;
    border-radius: 7px;
    max-width: 187px;
    height: 38px;
    text-transform: capitalize;
  }
}

.customer-title {
  display: flex;
  align-items: center;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
}

.divider {
  margin: 40px 0;
}

.product-card {
  font-family: 'Nunito Sans';
  margin: 20px 10px;
  padding: 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  &__title {
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    padding: 0;
    font-size: 16px;
  }
  &__content {
    display: flex;
    justify-content: space-between;
    padding: 0;
    font-size: 14px;
  }
}

.order-container {
  padding-bottom: 200px;
}
</style>
